import React, { useState } from "react";
import { useNavigate, Link, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { Leaf, AlertCircle } from "lucide-react";

const AuthPage = ({ mode }) => {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [errorAction, setErrorAction] = useState(null); // {label, to}

  const humanize = (raw) => {
    const s = formatApiError(raw) || "";
    const low = s.toLowerCase();
    // Order matters: check specific/exact strings first, generic fallbacks last.
    if (low.includes("invalid email or password")) return "Email or password is incorrect. Please try again.";
    if (low.includes("email already registered")) {
      setErrorAction({ label: "Sign in instead", to: "/login" });
      return "That email is already registered with Ecowallet. Try signing in instead.";
    }
    if (low.includes("value is not a valid email") || low.includes("value_error.email")) {
      return "Please enter a valid email address (e.g. name@example.com).";
    }
    if (s.includes("at least 6 characters")) return "Password must be at least 6 characters long.";
    if (s.includes("at least 1 character")) return "Please enter your full name.";
    if (low.includes("not authenticated") || low.includes("token expired")) return "Your session has expired. Please sign in again.";
    return s || "Something went wrong. Please try again.";
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setErrorAction(null);

    if (mode === "signup") {
      if (form.name.trim().length < 1) return setErrorMsg("Please enter your full name.");
      if (form.password.length < 6) return setErrorMsg("Password must be at least 6 characters long.");
      if (!agreed) return setErrorMsg("Please accept the Terms of Service and Privacy Policy to continue.");
    }

    setLoading(true);
    try {
      if (mode === "signup") {
        trackEvent("SIGNUP_STARTED");
        await register(form.name, form.email, form.password);
        localStorage.setItem("ecowallet_terms_accepted", new Date().toISOString());
        toast.success(`Welcome to Ecowallet, ${form.name}!`);
      } else {
        await login(form.email, form.password);
        toast.success("Welcome back!");
      }
      nav(next, { replace: true });
    } catch (e) {
      const msg = humanize(e.response?.data?.detail ?? e.message);
      setErrorMsg(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=1200&q=80" alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/80 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <Leaf className="h-8 w-8 mb-4" />
          <h2 className="text-3xl font-bold leading-tight">Every purchase can save the planet.</h2>
          <p className="mt-3 text-white/80">Join thousands earning cashback while making eco-friendly choices.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-14">
        <div className="w-full max-w-md">
          <h1 className="text-3xl font-bold tracking-tight">
            {mode === "signup" ? "Create your account" : "Welcome back"}
          </h1>
          <p className="text-muted-foreground mt-2">
            {mode === "signup" ? "Start earning Green Points instantly (100 pts welcome bonus)." : "Sign in to continue shopping sustainably."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid={`${mode}-form`}>
            {errorMsg && (
              <div
                className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 text-red-800 dark:text-red-200 px-4 py-3 text-sm"
                data-testid="auth-error"
                role="alert"
              >
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p>{errorMsg}</p>
                  {errorAction && (
                    <Link to={errorAction.to} className="mt-1 inline-block font-semibold underline" data-testid="auth-error-action">
                      {errorAction.label} →
                    </Link>
                  )}
                </div>
              </div>
            )}

            {mode === "signup" && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Name</label>
                <input
                  required
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="signup-name"
                />
              </div>
            )}
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Email</label>
              <input
                required type="email" autoComplete="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid={`${mode}-email`}
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Password</label>
              <input
                required type="password"
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid={`${mode}-password`}
              />
              {mode === "signup" && (
                <p className="mt-1.5 text-xs text-muted-foreground" data-testid="password-hint">
                  Minimum 6 characters.
                </p>
              )}
            </div>

            {mode === "signup" && (
              <label className="flex items-start gap-3 pt-2 cursor-pointer select-none" data-testid="terms-agreement">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-border accent-primary cursor-pointer"
                  data-testid="signup-terms-checkbox"
                />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" className="text-primary font-semibold hover:underline">Terms of Service</Link>
                  {" "}and{" "}
                  <Link to="/privacy" target="_blank" className="text-primary font-semibold hover:underline">Privacy Policy</Link>.
                </span>
              </label>
            )}

            <button type="submit" disabled={loading || (mode === "signup" && !agreed)} className="btn-pill w-full bg-primary text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`${mode}-submit`}>
              {loading ? "Please wait..." : mode === "signup" ? "Create account" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            {mode === "signup" ? (
              <>Already have an account? <Link to="/login" className="text-primary font-semibold hover:underline">Sign in</Link></>
            ) : (
              <>New here? <Link to="/signup" className="text-primary font-semibold hover:underline">Create an account</Link></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
